import React from 'react';
import { Progress } from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import { useAsync } from 'react-use';

import { useAnalytics } from '@backstage/core-plugin-api';
import { useDiscoveryApi } from './util/discovery';
import { Collection, TestResult } from 'plugin-test-results-common';
import { Grid } from '@material-ui/core';

import { TestResultsTable } from './TestResultsTable';
import { DAY_WINDOWS } from './config';

import {
  useQueryString,
  useQueryNumber,
  useQueryFlag,
} from 'plugin-react-util';

type TestResultsDataProps = {
  component: string;
  kind: string;
};

export const TestResultsDataView = ({
  component,
  kind,
}: TestResultsDataProps) => {
  const discoveryApi = useDiscoveryApi();

  const [collection, setCollection] = useQueryString('collection');
  const [days, setDays] = useQueryNumber('days', DAY_WINDOWS[0]);
  const [branch, setBranch] = useQueryString('branch', 'develop');
  const [onlyFailures, setOnlyFailures] = useQueryFlag('failed');
  const analytics = useAnalytics();

  const {
    value: collections,
    loading: collectionsLoading,
    error: collectionsError,
  } = useAsync(async (): Promise<Collection[]> => {
    const baseUrl = await discoveryApi.getBaseUrl('test-results');
    const response = await fetch(
      `${baseUrl}/collections/${kind}/${component}`,
      {
        credentials: 'include',
      },
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Collections fetch failed: ${response.status} - ${response.statusText}\n${text}`,
      );
    }
    return response.json();
  }, [component]);
  if (!collectionsLoading && !collection && collections && collections.length) {
    setCollection(collections[0].name);
  }

  const {
    value: testResults,
    loading: testResultsLoading,
    error: testResultsError,
  } = useAsync(async (): Promise<TestResult[]> => {
    if (!collection) {
      return [];
    }
    const start = Date.now();
    const baseUrl = await discoveryApi.getBaseUrl('test-results');
    const response = await fetch(
      `${baseUrl}/${kind}/${component}/${collection}?days=${days}`,
      {
        credentials: 'include',
      },
    );
    const loadTime = Date.now() - start;
    analytics.captureEvent('testResultsPageLoadTime', component, {
      value: loadTime,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Test results fetch failed: ${response.status} - ${response.statusText}\n${text}`,
      );
    }
    return response.json() as Promise<TestResult[]>;
  }, [collection, component, days]);

  const loading = collectionsLoading || testResultsLoading;
  const error = collectionsError || testResultsError;

  if (loading) {
    return <Progress />;
  } else if (collections && collections.length === 0) {
    return (
      <Alert severity="info">
        No test collections found for ${component}. Is{' '}
        <code>bni.test_collections</code> defined in the catalog info file?
      </Alert>
    );
  } else if (error) {
    return (
      <Alert severity="error">Could not fetch test data: {error.message}</Alert>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} key="test-results-table">
        <TestResultsTable
          testResults={testResults || []}
          control={{
            days,
            setDays: (val: number) => setDays(val),
            branch,
            setBranch: (val: string) => setBranch(val),
            collection,
            setCollection: (val: string) => setCollection(val),
            onlyFailures,
            setOnlyFailures: (val: boolean) => setOnlyFailures(val),
            availableCollections: new Set(
              (collections || []).map(coll => coll.name),
            ),
          }}
        />
      </Grid>
    </Grid>
  );
};
